import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, GraduationCap, User } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { EXPERT_CATEGORIES } from "@/i18n/translations";
import { useSeo } from "@/lib/seo";
import VerifiedBadge from "@/components/VerifiedBadge";

const selCls = "border border-tertiary bg-white px-3 py-2.5 text-sm outline-none focus:border-champagne";

export default function ExpertsList() {
  const { lang, L } = useLang();
  useSeo({
    title: lang === "fr" ? "Expertise RSTI" : "RSTI Expertise",
    description: lang === "fr"
      ? "Les compétences internes de RSTI par domaine d'expertise, fonction et rôle — l'expertise réelle qui structure et accompagne chaque dossier."
      : "RSTI's in-house competencies by domain of expertise, function and role — the real expertise behind every engagement.",
    lang,
  });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tax, setTax] = useState({ sector: [], market: [] });
  const [f, setF] = useState({ q: "", expertise: "", sector: "", country: "", verification: "" });

  useEffect(() => {
    Promise.all([api.get("/taxonomies", { params: { type: "sector" } }), api.get("/taxonomies", { params: { type: "market" } })])
      .then(([s, m]) => setTax({ sector: s.data, market: m.data })).catch(() => {});
  }, []);
  useEffect(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/expert-profiles", { params }).then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  }, [f]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div>
      <section className="border-b border-tertiary bg-sand">
        <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10 lg:py-16">
          <span className="text-xs uppercase tracking-[0.3em] text-champagne">RSTI</span>
          <h1 className="mt-3 font-serif text-4xl tracking-tight text-anthracite sm:text-5xl">{lang === "fr" ? "Expertise RSTI" : "RSTI Expertise"}</h1>
          <p className="mt-3 max-w-2xl text-anthracite/70">{lang === "fr"
            ? "Les compétences internes de RSTI — présentées par domaine d'expertise, fonction et rôle. L'expertise réelle qui structure, qualifie et accompagne chaque dossier."
            : "RSTI's in-house competencies — presented by domain of expertise, function and role. The real expertise that structures, qualifies and supports every engagement."}</p>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="expert-categories">
            {EXPERT_CATEGORIES.slice(0, 12).map((o) => (
              <button key={o.value} onClick={() => set("expertise", f.expertise === o.value ? "" : o.value)} className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${f.expertise === o.value ? "border-anthracite bg-anthracite text-ivory" : "border-tertiary bg-white text-anthracite/70 hover:border-champagne"}`}>{o[lang]}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <div className="mb-8 border border-tertiary bg-white p-4">
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-anthracite/40" />
            <input data-testid="expert-search" value={f.q} onChange={(e) => set("q", e.target.value)} placeholder={lang === "fr" ? "Rechercher un expert…" : "Search an expert…"} className="w-full border border-tertiary bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-champagne" />
          </div>
          <div className="flex flex-wrap gap-2">
            <select data-testid="filter-expertise" value={f.expertise} onChange={(e) => set("expertise", e.target.value)} className={selCls}><option value="">{lang === "fr" ? "Expertise" : "Expertise"}</option>{EXPERT_CATEGORIES.map((o) => <option key={o.value} value={o.value}>{o[lang]}</option>)}</select>
            <select value={f.sector} onChange={(e) => set("sector", e.target.value)} className={selCls}><option value="">{lang === "fr" ? "Secteur" : "Sector"}</option>{tax.sector.map((s) => <option key={s.slug} value={s.slug}>{s[`label_${lang}`]}</option>)}</select>
            <select value={f.country} onChange={(e) => set("country", e.target.value)} className={selCls}><option value="">{lang === "fr" ? "Marché" : "Market"}</option>{tax.market.map((s) => <option key={s.slug} value={s.slug}>{s[`label_${lang}`]}</option>)}</select>
          </div>
        </div>

        {loading ? <p className="py-16 text-center text-anthracite/40">…</p> : items.length === 0 ? (
          <div className="border border-dashed border-tertiary bg-white py-20 text-center" data-testid="expert-empty">
            <GraduationCap className="mx-auto mb-3 h-6 w-6 text-anthracite/30" />
            <p className="text-anthracite/50">{lang === "fr" ? "Aucune expertise publiée pour ces critères." : "No published expertise for these criteria."}</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map((o) => (
              <Link key={o.id} to={`/experts/${o.slug}`} data-testid={`expert-card-${o.slug}`} className="group flex flex-col border border-tertiary bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                <div className="flex flex-1 flex-col p-6">
                  <div className="mb-4 flex items-center gap-4">
                    {o.photo ? <img src={o.photo} alt="" className="h-14 w-14 shrink-0 rounded-full border border-tertiary object-cover" /> : <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-tertiary bg-sand"><User className="h-6 w-6 text-anthracite/30" /></div>}
                    <div>
                      <h3 className="font-serif text-xl leading-snug text-anthracite">{o.name}</h3>
                      <p className="text-xs text-anthracite/50">{L(o, "role")}{o.organization ? ` · ${o.organization}` : ""}</p>
                    </div>
                  </div>
                  <div className="mb-3 flex flex-wrap gap-1.5">
                    {(o.expertises || []).slice(0, 3).map((ex) => <span key={ex} className="rounded-full border border-tertiary bg-sand px-2 py-0.5 text-[10px] text-anthracite/60">{EXPERT_CATEGORIES.find((x) => x.value === ex)?.[lang] || ex}</span>)}
                  </div>
                  <p className="mb-4 flex-1 text-sm leading-relaxed text-anthracite/70 line-clamp-2">{L(o, "summary")}</p>
                  <div className="flex items-center justify-between border-t border-tertiary pt-4 text-xs text-anthracite/50">
                    <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{o.country || "—"}</span>
                    <VerifiedBadge verified={o.rst_verified} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
